import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { useClientProjectsHooks } from '../clientes/projects/api';
import { useLinkRcProjeto, useRcProjetos, useUnlinkRcProjeto } from './hooks';

export interface RcProjetosDialogProps {
  open: boolean;
  purchaseRequestId: number;
  clienteId: number | null;
  onClose: () => void;
}

/**
 * Manager de projetos vinculados à RC (associação N:N). Lista os vinculados,
 * permite vincular a partir dos projetos do cliente da RC (menos os já
 * vinculados) e desvincular. Card [uJoKy8Qk].
 */
export function RcProjetosDialog({
  open,
  purchaseRequestId,
  clienteId,
  onClose,
}: RcProjetosDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();

  const vinculadosQuery = useRcProjetos(purchaseRequestId);
  const projectsHooks = useClientProjectsHooks(clienteId ?? 0);
  const disponiveisQuery = projectsHooks.useList({
    page: 1,
    pageSize: 200,
    sortBy: 'nome',
    sortOrder: 'asc',
  });
  const link = useLinkRcProjeto(purchaseRequestId);
  const unlink = useUnlinkRcProjeto(purchaseRequestId);

  const [selecionado, setSelecionado] = useState<number | ''>('');

  const vinculados = vinculadosQuery.data ?? [];
  const vinculadosSet = useMemo(
    () => new Set(vinculados.map((v) => v.clienteProjetoCodigo)),
    [vinculados],
  );
  // Disponíveis = projetos do cliente que ainda não estão vinculados.
  const disponiveis = useMemo(
    () => (disponiveisQuery.data?.data ?? []).filter((p) => !vinculadosSet.has(p.id)),
    [disponiveisQuery.data, vinculadosSet],
  );

  const isBusy = link.isPending || unlink.isPending;

  async function handleAdd(): Promise<void> {
    if (selecionado === '') return;
    try {
      await link.mutateAsync(selecionado);
      setSelecionado('');
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function handleRemove(codigo: number): Promise<void> {
    try {
      await unlink.mutateAsync(codigo);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setSelecionado('') }}
    >
      <DialogTitle>{t('features.requisicoesCompra.projetos.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('features.requisicoesCompra.projetos.descricao')}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              select
              size="small"
              fullWidth
              label={t('features.requisicoesCompra.projetos.adicionarLabel')}
              value={selecionado === '' ? '' : String(selecionado)}
              onChange={(e) => setSelecionado(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={disponiveisQuery.isFetching || disponiveis.length === 0}
              helperText={
                disponiveis.length === 0 && !disponiveisQuery.isFetching
                  ? t('features.requisicoesCompra.projetos.semDisponiveis')
                  : undefined
              }
            >
              <MenuItem value="">
                <em>{t('features.requisicoesCompra.projetos.selecionar')}</em>
              </MenuItem>
              {disponiveis.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.nome}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => void handleAdd()}
              disabled={selecionado === '' || isBusy}
              sx={{ mt: 0.5, flexShrink: 0 }}
            >
              {t('features.requisicoesCompra.projetos.adicionar')}
            </Button>
          </Stack>

          {vinculadosQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : vinculados.length === 0 ? (
            <Alert severity="info" variant="outlined">
              {t('features.requisicoesCompra.projetos.vazio')}
            </Alert>
          ) : (
            <List dense disablePadding>
              {vinculados.map((v) => (
                <ListItem
                  key={v.clienteProjetoCodigo}
                  divider
                  secondaryAction={
                    <Tooltip title={t('features.requisicoesCompra.projetos.remover')}>
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          disabled={isBusy}
                          aria-label={t('features.requisicoesCompra.projetos.removerAria', {
                            projeto: v.clienteProjetoNome,
                          })}
                          onClick={() => void handleRemove(v.clienteProjetoCodigo)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText primary={v.clienteProjetoNome} />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
